import { useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, RotateCcw, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { isAndroidApp } from "@/lib/platform";
import { AdSlot } from "@/components/ads/AdSlot";
import { ADSENSE_SLOT_BANNER } from "@/lib/should-show-ads";

const SUPPORT_EMAIL = "support@fitauraai.com";

const Refund = () => {
  const navigate = useNavigate();
  const onAndroid = isAndroidApp();

  // SEO handled by <SEO /> component below.

  return (
    <>
      <SEO />
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-primary/10 blur-[140px]" />
      </div>
      <header className="sticky top-0 z-30 border-b border-white/5 bg-background/85 px-5 pt-[max(env(safe-area-inset-top),10px)] pb-3 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-2xl items-center gap-2.5">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            aria-label="Back"
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-primary/30 to-accent/20 ring-1 ring-primary/30 shadow-[0_0_18px_-4px_hsl(var(--primary)/0.55)]">
            <img src="/app-icon-128.png" alt="FitAura AI" className="h-full w-full object-cover" />
          </span>
          <div className="min-w-0">
            <span className="font-display block text-[15px] font-bold leading-none tracking-tight">
              FitAura<span className="text-primary"> AI</span>
            </span>
            <span className="font-display mt-1 block truncate text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Refund Policy
            </span>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl px-5 py-6">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-[0_0_24px_-6px_hsl(var(--primary))]">
            <RotateCcw className="h-6 w-6" />
          </div>
          <div>
            <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
              — REFUNDS
            </span>
            <h2 className="font-display mt-0.5 text-2xl font-bold uppercase tracking-tight">30-day money-back guarantee.</h2>
            <p className="text-xs text-muted-foreground">Last updated: April 20, 2026</p>
          </div>
        </div>

        <Card className="space-y-6 p-5 shadow-soft">
          <section>
            <h3 className="mb-2 text-base font-bold">1. Failed generations</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              If a generation fails on our side (server error, model failure, image processing
              error), the credit is automatically refunded to your balance — no action needed
              on your part.
            </p>
          </section>

          {onAndroid ? (
            <section>
              <h3 className="mb-2 text-base font-bold">2. Android in-app purchases</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                On Android, all credit purchases are processed by{" "}
                <span className="font-semibold text-foreground">Google Play Billing</span>.
                Refund requests for Android purchases are handled directly by Google Play
                under their refund policy.
              </p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                To request a refund:
              </p>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
                <li>
                  Open{" "}
                  <a href="https://play.google.com/store/account/orderhistory" target="_blank" rel="noopener noreferrer" className="underline">
                    Google Play order history
                  </a>{" "}
                  and select the purchase to request a refund.
                </li>
                <li>
                  Or contact us at{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}?subject=FitAura%20AI%20refund%20request`} className="underline">
                    {SUPPORT_EMAIL}
                  </a>{" "}
                  from your registered email and we will help guide you.
                </li>
              </ul>
            </section>
          ) : (
            <>
              <section>
                <h3 className="mb-2 text-base font-bold">2. International orders</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  For purchases made outside India on the web, your payment is processed by
                  our online reseller and Merchant of Record. Refunds for those orders are
                  issued by the reseller on our behalf.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  To request a refund, contact us at{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}?subject=FitAura%20AI%20refund%20request`} className="underline">
                    {SUPPORT_EMAIL}
                  </a>{" "}
                  from your registered email and we will help process it.
                </p>
              </section>

              <section>
                <h3 className="mb-2 text-base font-bold">3. India orders (Razorpay)</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  For purchases made in India through Razorpay, email us at{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}?subject=FitAura%20AI%20refund%20request`} className="underline">
                    {SUPPORT_EMAIL}
                  </a>{" "}
                  from your registered email within 30 days of purchase. Approved refunds are
                  processed back to your original payment method via Razorpay, typically within
                  5–10 business days.
                </p>
              </section>
            </>
          )}

          <section>
            <h3 className="mb-2 text-base font-bold">4. What we may not refund</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              We reserve the right to deny or pro-rate refund requests where:
            </p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
              <li>The vast majority of credits in the pack have already been consumed.</li>
              <li>The account has been suspended for violating our{" "}
                <Link to="/terms" className="underline">Terms of Service</Link> (e.g. uploading content of others without consent, deepfakes, IP infringement).
              </li>
              <li>The request is clearly fraudulent or abusive (e.g. repeated refund requests after consuming all credits).</li>
            </ul>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">5. Duplicate or erroneous charges</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              If you were charged twice for the same purchase or charged in error, contact us
              within 14 days and we will refund the duplicate charge in full.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">6. Contact</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              For any refund question:
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=FitAura%20AI%20refund%20request`}
              className="mt-2 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-accent"
            >
              <Mail className="h-4 w-4 text-primary" />
              {SUPPORT_EMAIL}
            </a>
          </section>
        </Card>
        <AdSlot slot={ADSENSE_SLOT_BANNER} format="auto" className="mt-6" />

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/" className="underline-offset-2 hover:underline">Back to FitAura AI</Link>
          {" · "}
          <Link to="/terms" className="underline-offset-2 hover:underline">Terms</Link>
          {" · "}
          <Link to="/privacy" className="underline-offset-2 hover:underline">Privacy</Link>
        </p>
      </main>
    </div>
    </>
  );
};

export default Refund;
